import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { ContractApiResponse } from '../../../services/contract-services';
import { getPaymentHistory, PaymentHistoryItemNew } from '../../../services/payment-services';
import { hideLoader, showLoader } from '../../../store/reducers/appSlice';
import { openToast } from '../../../store/reducers/toastSlice';
import { convertHTMLToPDF } from '../../../utils/autopay-pdf-generator';
import { trackExport } from '../../../utils/google-analytics';
import { formatCurrency, formatInET, formatToUSDate } from '../../../utils/helper';
import { useTranslation } from '../../../utils/translations';
import {
    buildCsvContent,
    buildXlsxData,
    dispatchExportError,
    findExportButtonElement,
} from '../contractDetailsHelpers';

export const usePaymentHistoryExport = (contract: ContractApiResponse | null, isImpersonating: boolean) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Payment History State
    const [paymentHistoryData, setPaymentHistoryData] = useState<PaymentHistoryItemNew[]>([]);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);

    // Payment History Export Format State
    const [exportPaymentHistoryFormat, setExportPaymentHistoryFormat] = useState<string>('');
    const [exportPaymentHistoryAnchorEl, setExportPaymentHistoryAnchorEl] = useState<null | HTMLElement>(null);

    // Fetch Payment History - Fetch all data at once for client-side pagination
    const fetchPaymentHistory = useCallback(async () => {
        if (!contract?.Id) return;
        setPaymentHistoryLoading(true);
        try {
            // Fetch all data with a large limit (1000 should be enough for most cases)
            const response = await getPaymentHistory(contract.Id, true);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setPaymentHistoryData(response.data);
            }
        } catch (err) {
            console.error('Error fetching payment history:', err);
        } finally {
            setPaymentHistoryLoading(false);
            dispatch(hideLoader());
        }
    }, [contract?.Id, dispatch]);

    // Transform payment history data for export
    const getTransformedPaymentHistoryData = useCallback(() => {
        if (!paymentHistoryData || paymentHistoryData.length === 0) {
            return [];
        }

        const sortedPayments = [...paymentHistoryData].sort((a, b) => {
            const dateA = a.paymentDate || '';
            const dateB = b.paymentDate || '';
            return new Date(dateB).getTime() - new Date(dateA).getTime();
        });

        return sortedPayments.map((payment: PaymentHistoryItemNew) => {
            const paymentAmount = Number(payment.amount) || 0;
            const hasInvoices = payment.invoices && payment.invoices.length > 0;
            const hasReturnedInvoice = hasInvoices && payment.invoices.some(inv => inv.status === 'Returned');
            const firstInvoiceStatus = hasInvoices ? payment.invoices[0].status : '';

            return {
                paymentDate: payment.paymentDate ? formatToUSDate(payment.paymentDate) : 'N/A',
                amount: paymentAmount, // Keep as number for calculations
                amountFormatted: formatCurrency(paymentAmount),
                paymentType: payment.paymentType || 'N/A',
                referenceNumber: payment.referenceNumber || 'N/A',
                status: hasReturnedInvoice ? 'Returned' : firstInvoiceStatus,
            };
        });
    }, [paymentHistoryData]);

    // Shared "no data" toast for all payment history export formats
    const showNoPaymentHistoryDataToast = useCallback(() => {
        dispatch(
            openToast({
                show: true,
                message: t('payments.paymentHistory.noData') || 'No Payment History Data Available to Export',
                type: 'warning',
            }),
        );
    }, [dispatch, t]);

    // Shared column definitions for CSV/XLSX export
    const getPaymentHistoryColumns = useCallback(
        () => [
            { id: 'paymentDate', label: t('contracts.paymentHistory.effectiveDate') || 'Effective Date' },
            { id: 'referenceNumber', label: t('contracts.paymentHistory.referenceNumber') || 'Reference Number' },
            { id: 'paymentType', label: t('contracts.paymentHistory.method') || 'Method' },
            { id: 'amountFormatted', label: t('contracts.paymentHistory.amount') || 'Amount' },
            { id: 'status', label: 'Status' },
        ],
        [t],
    );

    // Export Payment History to CSV
    const handleExportPaymentHistoryCSV = useCallback(async () => {
        if (isImpersonating) return;
        if (!paymentHistoryData || paymentHistoryData.length === 0) {
            showNoPaymentHistoryDataToast();
            return;
        }

        try {
            const transformedPayments = getTransformedPaymentHistoryData();
            const columns = getPaymentHistoryColumns();

            // Calculate total amount
            const totalAmount = transformedPayments.reduce((sum, payment) => sum + payment.amount, 0);
            const totalRowCells = ['', '', 'Total', `"${formatCurrency(totalAmount)}"`, ''];

            const csvContent = buildCsvContent(columns, transformedPayments, totalRowCells);

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `payment-history-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            trackExport('CSV', 'Payment History');
        } catch (err) {
            console.error('Error exporting payment history to CSV:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Payment History. Please Try Again.');
        }
    }, [
        isImpersonating,
        paymentHistoryData,
        dispatch,
        getTransformedPaymentHistoryData,
        getPaymentHistoryColumns,
        showNoPaymentHistoryDataToast,
    ]);

    // Export Payment History to Excel
    const handleExportPaymentHistoryXLSX = useCallback(async () => {
        if (isImpersonating) return;
        if (!paymentHistoryData || paymentHistoryData.length === 0) {
            showNoPaymentHistoryDataToast();
            return;
        }

        try {
            const transformedPayments = getTransformedPaymentHistoryData();
            const headerLabels = getPaymentHistoryColumns().map(col => col.label);

            // Calculate total amount
            const totalAmount = transformedPayments.reduce((sum, payment) => sum + payment.amount, 0);

            // Prepare data for Excel
            const wsData = buildXlsxData(
                headerLabels,
                transformedPayments,
                payment => [
                    payment.paymentDate,
                    payment.referenceNumber,
                    payment.paymentType,
                    payment.amountFormatted,
                    payment.status,
                ],
                ['', '', 'Total', formatCurrency(totalAmount), ''],
            );

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Payment History');
            XLSX.writeFile(wb, `payment-history-${new Date().toISOString().split('T')[0]}.xlsx`);

            trackExport('XLSX', 'Payment History');
        } catch (err) {
            console.error('Error exporting payment history to XLSX:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Payment History. Please Try Again.');
        }
    }, [
        isImpersonating,
        paymentHistoryData,
        dispatch,
        getTransformedPaymentHistoryData,
        getPaymentHistoryColumns,
        showNoPaymentHistoryDataToast,
    ]);

    // Export Payment History to PDF
    const handleExportPaymentHistoryPDF = useCallback(async () => {
        if (isImpersonating) return;
        if (!paymentHistoryData || paymentHistoryData.length === 0) {
            showNoPaymentHistoryDataToast();
            return;
        }

        try {
            dispatch(showLoader());
            const transformedPayments = getTransformedPaymentHistoryData();

            // Calculate total amount
            const totalAmount = transformedPayments.reduce((sum, payment) => sum + payment.amount, 0);

            // Build table rows HTML
            const tableRows = transformedPayments
                .map(
                    payment => `
                    <tr>
                        <td>${payment.paymentDate}</td>
                        <td>${payment.referenceNumber}</td>
                        <td>${payment.paymentType}</td>
                        <td>${payment.amountFormatted}</td>
                        <td>${payment.status}</td>
                    </tr>
                `,
                )
                .join('');

            const totalRow = `
                <tr style="font-weight: bold; border-top: 2px solid #333;">
                    <td colspan="3"><strong>Total</strong></td>
                    <td>${formatCurrency(totalAmount)}</td>
                    <td></td>
                </tr>
            `;

            const htmlContent = `
                <html>
                    <head>
                        <title>Payment History - ${contract?.AspireId || 'Contract'}</title>
                        <style>
                            body {
                                font-family: Arial, sans-serif;
                                margin: 0;
                            }
                            h1 {
                                margin-bottom: 20px;
                                color: #333;
                            }
                            table {
                                width: 100%;
                                border-collapse: collapse;
                            }
                            th, td {
                                border-bottom: 1px solid #ddd;
                                padding: 10px;
                                text-align: left;
                            }
                            th {
                                background-color: #f2f2f2;
                                font-weight: bold;
                            }
                        </style>
                    </head>
                    <body>
                        <h1>Payment History</h1>
                        <p><strong>Contract ID:</strong> ${contract?.AspireId || contract?.Id || 'N/A'}</p>
                        <p><strong>Date:</strong> ${formatInET(new Date())}</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>${t('contracts.paymentHistory.effectiveDate') || 'Effective Date'}</th>
                                    <th>${t('contracts.paymentHistory.referenceNumber') || 'Reference Number'}</th>
                                    <th>${t('contracts.paymentHistory.method') || 'Method'}</th>
                                    <th>${t('contracts.paymentHistory.amount') || 'Amount'}</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${tableRows}
                                ${totalRow}
                            </tbody>
                        </table>
                    </body>
                </html>
            `;

            // Convert HTML to PDF
            const pdfBlob = await convertHTMLToPDF(htmlContent);

            if (!pdfBlob || pdfBlob.size === 0) {
                throw new Error('PDF generation failed');
            }

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(pdfBlob);
            link.setAttribute('href', url);
            link.setAttribute('download', `payment-history-${new Date().toISOString().split('T')[0]}.pdf`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            trackExport('PDF', 'Payment History');
        } catch (err) {
            console.error('Error exporting payment history to PDF:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Payment History. Please Try Again.');
        } finally {
            dispatch(hideLoader());
        }
    }, [
        isImpersonating,
        paymentHistoryData,
        contract,
        dispatch,
        t,
        getTransformedPaymentHistoryData,
        showNoPaymentHistoryDataToast,
    ]);

    // Open export payment history dropdown menu
    const handleExportPaymentHistoryMenu = (event?: React.MouseEvent<HTMLElement> | React.SyntheticEvent) => {
        if (isImpersonating) return;
        const exportButton = findExportButtonElement(event, 'icon-arrow-drop-down');

        if (exportButton) {
            setExportPaymentHistoryAnchorEl(exportButton);
        }
    };

    // Close export menu
    const handleCloseExportPaymentHistoryMenu = () => {
        setExportPaymentHistoryAnchorEl(null);
    };

    // Handle export format selection from menu
    const handleExportPaymentHistoryFormatSelect = async (format: string) => {
        if (isImpersonating) {
            setExportPaymentHistoryAnchorEl(null);
            return;
        }
        setExportPaymentHistoryAnchorEl(null);
        setExportPaymentHistoryFormat(format);
    };

    // Handle export format selection
    useEffect(() => {
        if (!exportPaymentHistoryFormat) return;

        const executeExport = async () => {
            switch (exportPaymentHistoryFormat) {
                case 'CSV':
                    await handleExportPaymentHistoryCSV();
                    break;
                case 'Excel':
                    await handleExportPaymentHistoryXLSX();
                    break;
                case 'PDF':
                    await handleExportPaymentHistoryPDF();
                    break;
                default:
                    break;
            }
            // Reset the format after export
            setExportPaymentHistoryFormat('');
        };

        executeExport();
    }, [
        exportPaymentHistoryFormat,
        handleExportPaymentHistoryCSV,
        handleExportPaymentHistoryXLSX,
        handleExportPaymentHistoryPDF,
    ]);

    return {
        paymentHistoryData,
        paymentHistoryLoading,
        fetchPaymentHistory,
        exportPaymentHistoryAnchorEl,
        handleExportPaymentHistoryMenu,
        handleCloseExportPaymentHistoryMenu,
        handleExportPaymentHistoryFormatSelect,
    };
};
